"use client";

import { useEffect, useState } from "react";
import Icon from "@/components/ui/AppIcon";

interface FAQItem {
  id: string;
  question: string;
  answer: string;
}

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -5% 0px" }
    );

    document.querySelectorAll(".animate-on-scroll").forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const faqs: FAQItem[] = [
    {
      id: "faq_1",
      question: "What types of productions do you specialize in?",
      answer:
        "We specialize in film production (feature films, short films, documentaries), event management (corporate events, concerts, award ceremonies, festivals), and post-production services (editing, color grading, sound design, visual effects). We handle projects of all scales from independent productions to large commercial ventures.",
    },
    {
      id: "faq_2",
      question: "How long does a typical production take?",
      answer:
        "Timeline varies by project scope. Small productions (corporate videos, event coverage) typically take 2-4 weeks. Medium productions (commercials, music videos) take 4-8 weeks. Large productions (feature films, major events) can take 3-6 months or more. We provide detailed timelines during initial consultation.",
    },
    {
      id: "faq_3",
      question: "Do you provide equipment rental separately?",
      answer:
        "Yes, we offer equipment rental services separately for clients who have their own crew. Our inventory includes professional cinema cameras, lighting packages, audio equipment, and grip gear. Contact us for detailed equipment lists and rental rates.",
    },
    {
      id: "faq_4",
      question: "What is your pricing structure?",
      answer:
        "We offer three main tiers: Basic (starting from $5,000), Professional (starting from $15,000), and Premium (custom quotes). Pricing depends on project scope, duration, crew size, equipment needs, and post-production requirements. We provide detailed quotes after initial consultation.",
    },
    {
      id: "faq_5",
      question: "Do you work with clients outside Mumbai and Delhi?",
      answer:
        "Absolutely! While our offices are in Mumbai and Delhi, we work on projects across India and internationally. We have experience coordinating remote productions and can travel to your location with our crew and equipment.",
    },
    {
      id: "faq_6",
      question: "How many revision rounds are included?",
      answer:
        "Basic package includes up to 3 revision rounds. Professional package includes unlimited revisions. Premium package includes unlimited revisions plus dedicated project manager. Additional revisions beyond package limits can be purchased separately.",
    },
    {
      id: "faq_7",
      question: "What formats do you deliver final projects in?",
      answer:
        "We deliver in industry-standard formats based on your needs: HD (1920x1080), 4K (3840x2160), or 8K (7680x4320) resolution. File formats include ProRes, H.264, H.265, and DCP for theatrical releases. We also provide web-optimized versions for online distribution.",
    },
    {
      id: "faq_8",
      question: "Do you offer payment plans?",
      answer:
        "Yes, we offer flexible payment plans for larger projects. Typically, we require 30% upfront, 40% at production start, and 30% upon final delivery. For long-term projects, we can arrange monthly payment schedules. Contact us to discuss custom payment arrangements.",
    },
  ];

  return (
    <section id="careers" className="py-24 md:py-32 px-6 md:px-12 bg-[#080808] border-t border-white/5">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-16 animate-on-scroll [animation:animationIn_0.8s_ease-out_0.2s_both]">
          <h2 className="text-3xl md:text-4xl font-display font-light text-white mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-muted-foreground font-light">
            Find answers to common questions about our services and process.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={faq.id}
              className={`glass-card rounded-2xl overflow-hidden transition-all duration-300 animate-on-scroll [animation:animationIn_0.8s_ease-out_${
                0.3 + index * 0.05
              }s_both]`}
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-white/5 transition-colors"
              >
                <span className="text-white font-medium pr-4">{faq.question}</span>
                <Icon
                  name="ChevronDownIcon"
                  size={20}
                  className={`text-primary flex-shrink-0 transition-transform duration-300 ${
                    openIndex === index ? "rotate-180" : ""
                  }`}
                />
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? "max-h-96" : "max-h-0"
                }`}
              >
                <div className="px-6 pb-6">
                  <p className="text-muted-foreground leading-relaxed">{faq.answer}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center animate-on-scroll [animation:animationIn_0.8s_ease-out_0.8s_both]">
          <p className="text-muted-foreground mb-4">Still have questions?</p>
          <a
            href="#"
            className="inline-flex items-center gap-2 text-primary font-medium hover:gap-3 transition-all"
          >
            Contact Our Team
            <Icon name="ArrowRightIcon" size={16} />
          </a>
        </div>
      </div>
    </section>
  );
}